/* eslint-disable no-console */
import React from "react";
import moment from "moment";
import { notification } from "antd";
import { useSelector } from "react-redux";
import {
  dataFromSnapshot,
  fetchCustomerBookingsFromFirestore,
  fetchCustomerCouponsFromFirestore,
  fetchCustomerFavoritesFromFirestore,
  updateCustomerProfileInFirestore,
} from "../../../firestore/firestoreService";
import { signOutFirebase } from "../../../firestore/firebaseService";
import { updateCustomerPasswordInFirestore } from "../../../firestore/firestoreService/Customer14860Store";
import {
  IBookingPackage,
  ICustomerCoupon37856,
  ICustomerFavorite,
} from "../../../common/types";
import {
  CustomerWrapper,
  CustomerContent,
  CustomerGreeting,
  CustomerSubTitle,
  CustomerPanel,
  CustomerSectionHeader,
  CustomerSectionTitle,
} from "./Customer.style";
import { CustomerSidebar } from "./CustomerSidebar";
import { CustomerProfileForm } from "./CustomerProfileForm";
import { CustomerBookings } from "./CustomerBookings";
import { CustomerFavorites } from "./CustomerFavorites";
import { CustomerCoupons } from "./CustomerCoupons";
import { CustomerPasswordModal } from "./CustomerPasswordModal";
import { CustomerBookingDetails } from "./CustomerBookingDetails";

const partnerLocalStorage: Record<string, string> = {
  SYKS: "syksUserId",
  POED: "poedUserId",
  PASYDY: "userId",
};

const partnerId = process.env.REACT_APP_SITE_ID || "";
const partnerStorageKey = partnerLocalStorage[partnerId] || "userId";

const sidebarItems = [
  { key: "profile", label: "Προφίλ" },
  { key: "bookings", label: "Οι κρατήσεις μου" },
  { key: "favorites", label: "Αγαπημένα" },
  { key: "coupons", label: "Εκπτωτικά κουπόνια" },
];

export const CustomerPortal: React.FC = () => {
  const { selectedCustomer14860 } = useSelector(
    (state: any) => state.customers,
  );
  const customerId = selectedCustomer14860?.id;
  const [activeSection, setActiveSection] = React.useState<string>("profile");
  const [bookings, setBookings] = React.useState<IBookingPackage[]>([]);
  const [favorites, setFavorites] = React.useState<ICustomerFavorite[]>([]);
  const [coupons, setCoupons] = React.useState<ICustomerCoupon37856[]>([]);
  const [bookingsLoading, setBookingsLoading] = React.useState(false);
  const [favoritesLoading, setFavoritesLoading] = React.useState(false);
  const [couponsLoading, setCouponsLoading] = React.useState(false);
  const [profileLoading, setProfileLoading] = React.useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = React.useState(false);
  const [passwordLoading, setPasswordLoading] = React.useState(false);
  const [selectedBooking, setSelectedBooking] =
    React.useState<IBookingPackage | null>(null);

  React.useEffect(() => {
    if (!customerId) return undefined;
    const query = fetchCustomerBookingsFromFirestore(customerId);
    if (!query) return undefined;
    setBookingsLoading(true);
    const unsubscribe = query.onSnapshot(
      (snapshot: any) => {
        setBookings(snapshot.docs.map((doc: any) => dataFromSnapshot(doc)));
        setBookingsLoading(false);
      },
      (error: any) => {
        console.log(error);
        setBookingsLoading(false);
        notification.error({
          message: "Αποτυχία φόρτωσης κρατήσεων",
        });
      },
    );
    return unsubscribe;
  }, [customerId]);

  React.useEffect(() => {
    if (!customerId) return undefined;
    const query = fetchCustomerFavoritesFromFirestore(customerId);
    if (!query) return undefined;
    setFavoritesLoading(true);
    const unsubscribe = query.onSnapshot(
      (snapshot: any) => {
        setFavorites(snapshot.docs.map((doc: any) => dataFromSnapshot(doc)));
        setFavoritesLoading(false);
      },
      (error: any) => {
        console.log(error);
        setFavoritesLoading(false);
        notification.error({
          message: "Αποτυχία φόρτωσης αγαπημένων",
        });
      },
    );
    return unsubscribe;
  }, [customerId]);

  React.useEffect(() => {
    if (!customerId) return undefined;
    const query = fetchCustomerCouponsFromFirestore(customerId);
    if (!query) return undefined;
    setCouponsLoading(true);
    const unsubscribe = query.onSnapshot(
      (snapshot: any) => {
        setCoupons(snapshot.docs.map((doc: any) => dataFromSnapshot(doc)));
        setCouponsLoading(false);
      },
      (error: any) => {
        console.log(error);
        setCouponsLoading(false);
        notification.error({
          message: "Αποτυχία φόρτωσης κουπονιών",
        });
      },
    );
    return unsubscribe;
  }, [customerId]);

  const handleProfileSubmit = async (values: any) => {
    if (!customerId) return;
    try {
      setProfileLoading(true);
      const [firstName, ...rest] = values.nameAndSurname.trim().split(" ");
      const surname = rest.join(" ").trim();
      const dateOfBirth = values.dateOfBirth
        ? moment.isMoment(values.dateOfBirth)
          ? values.dateOfBirth.toDate()
          : values.dateOfBirth
        : null;
      await updateCustomerProfileInFirestore(customerId, {
        name: firstName || selectedCustomer14860?.name,
        surname: surname || selectedCustomer14860?.surname,
        nameAndSurname: values.nameAndSurname.trim(),
        iDPassportNumber: values.iDPassportNumber,
        email: values.email,
        phone: values.phone,
        secondaryPhone: values.secondaryPhone,
        dateOfBirth,
        newsletterOptIn: values.newsletterOptIn,
      });
      notification.success({
        message: "Τα στοιχεία σας ενημερώθηκαν",
      });
    } catch (error: any) {
      notification.error({
        message: "Κάτι πήγε στραβά",
        description: error?.message || "Δεν ήταν δυνατή η ενημέρωση",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async ({
    currentPassword,
    newPassword,
  }: {
    currentPassword: string;
    newPassword: string;
  }) => {
    if (!customerId) return;
    try {
      setPasswordLoading(true);
      await updateCustomerPasswordInFirestore({
        customerId,
        currentPassword,
        newPassword,
      });
      notification.success({
        message: "Ο κωδικός πρόσβασης ενημερώθηκε",
      });
      setPasswordModalOpen(false);
    } catch (error: any) {
      notification.error({
        message: "Δεν ήταν δυνατή η αλλαγή κωδικού",
        description: error?.message || "Ελέγξτε τα στοιχεία και δοκιμάστε ξανά",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOutFirebase();
    } finally {
      localStorage.removeItem(partnerStorageKey);
      window.location.href = "/";
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case "bookings":
        return (
          <>
            <CustomerSectionHeader>
              <CustomerSectionTitle>Οι κρατήσεις μου</CustomerSectionTitle>
            </CustomerSectionHeader>
            <CustomerBookings
              bookings={bookings}
              loading={bookingsLoading}
              onViewBooking={setSelectedBooking}
            />
          </>
        );
      case "favorites":
        return (
          <>
            <CustomerSectionHeader>
              <CustomerSectionTitle>Αγαπημένα</CustomerSectionTitle>
            </CustomerSectionHeader>
            <CustomerFavorites
              favorites={favorites}
              loading={favoritesLoading}
            />
          </>
        );
      case "coupons":
        return (
          <>
            <CustomerSectionHeader>
              <CustomerSectionTitle>Εκπτωτικά κουπόνια</CustomerSectionTitle>
            </CustomerSectionHeader>
            <CustomerCoupons coupons={coupons} loading={couponsLoading} />
          </>
        );
      case "profile":
      default:
        return (
          <CustomerProfileForm
            customer={selectedCustomer14860}
            loading={profileLoading}
            onSubmit={handleProfileSubmit}
            onResetPassword={() => setPasswordModalOpen(true)}
          />
        );
    }
  };

  if (!selectedCustomer14860) {
    return (
      <CustomerWrapper>
        <CustomerSidebar
          items={sidebarItems}
          activeKey={activeSection}
          onSelect={setActiveSection}
          onLogout={handleLogout}
          disabled
        />
        <CustomerContent>
          <CustomerPanel>
            <CustomerSectionTitle>
              Παρακαλώ συνδεθείτε για να συνεχίσετε
            </CustomerSectionTitle>
          </CustomerPanel>
        </CustomerContent>
      </CustomerWrapper>
    );
  }

  const customerDisplayName =
    selectedCustomer14860?.name || selectedCustomer14860?.nameAndSurname || "";

  return (
    <CustomerWrapper>
      <CustomerSidebar
        items={sidebarItems}
        activeKey={activeSection}
        onSelect={setActiveSection}
        onLogout={handleLogout}
      />
      <CustomerContent>
        <CustomerGreeting>{`Welcome, ${customerDisplayName}`}</CustomerGreeting>
        <CustomerSubTitle>
          Διαχειριστείτε το προφίλ, τις κρατήσεις και τα προνόμια σας
        </CustomerSubTitle>
        <CustomerPanel>{renderSection()}</CustomerPanel>
      </CustomerContent>
      <CustomerPasswordModal
        open={passwordModalOpen}
        loading={passwordLoading}
        onClose={() => setPasswordModalOpen(false)}
        onSubmit={handlePasswordSubmit}
      />
      <CustomerBookingDetails
        booking={selectedBooking}
        open={!!selectedBooking}
        onClose={() => setSelectedBooking(null)}
      />
    </CustomerWrapper>
  );
};
